import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Masonry from 'react-masonry-css';
import { projects } from '../data/projects';
import SEO from './SEO';

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lightboxImage, setLightboxImage] = React.useState<number | null>(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Close lightbox on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxImage(null);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  const openLightbox = (index: number) => {
    setLightboxImage(index);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  const nextImage = () => {
    if (lightboxImage !== null && project?.images) {
      setLightboxImage((lightboxImage + 1) % project.images.length);
    }
  };

  const prevImage = () => {
    if (lightboxImage !== null && project?.images) {
      setLightboxImage((lightboxImage - 1 + project.images.length) % project.images.length);
    }
  };

  const project = projects.find(p => p.id === id);

  if (!project) {
    return (
      <div className="project-detail-not-found">
        <div className="max-w-4xl mx-auto px-6 py-32 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Project Not Found</h1>
          <p className="text-gray-600 mb-8">The project you're looking for doesn't exist.</p>
          <button
            onClick={() => {
              navigate('/');
              setTimeout(() => {
                const element = document.getElementById('projects');
                if (element) {
                  const offset = 80;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }
              }, 100);
            }}
            className="project-detail-back-btn"
          >
            ← Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="project-detail-page">
      <SEO
        title={`${project.title} - ${project.client}`}
        description={project.longDescription || project.description}
        keywords={`${project.technologies.join(', ')}, ${project.category}, web development, software consulting`}
        url={`https://latantconsulting.com/projects/${project.id}`}
        image={project.imageUrl}
        type="article"
      />
      {/* Hero Section */}
      <section className="project-detail-hero">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <button
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  const element = document.getElementById('projects');
                  if (element) {
                    const offset = 80;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - offset;
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
              className="project-detail-back-btn mb-8"
            >
              ← Back to Projects
            </button>

            <div className="mb-6">
              <span className="project-detail-category">{project.category}</span>
              {project.featured && (
                <span className="featured-badge-detail">Featured</span>
              )}
            </div>

            <h1 className="project-detail-title">{project.title}</h1>
            <p className="project-detail-client">
              Client: <span className="font-semibold">{project.client}</span>
            </p>
            <p className="project-detail-date">
              {project.completedDate === 'Ongoing' ? 'Status: Ongoing' : `Completed: ${new Date(project.completedDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Project Image Gallery - Pinterest Style Masonry */}
      <motion.section
        className="project-detail-image-section"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <div className="max-w-6xl mx-auto px-6">
          {project.images && project.images.length > 0 ? (
            <Masonry
              breakpointCols={{
                default: 3,
                1100: 2,
                640: 1
              }}
              className="masonry-grid"
              columnClassName="masonry-grid-column"
            >
              {project.images.map((image, index) => (
                <motion.div
                  key={index}
                  className="masonry-item"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={image}
                    alt={`${project.title} screenshot ${index + 1}`}
                    className="masonry-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const placeholder = target.nextElementSibling as HTMLElement;
                      if (placeholder) placeholder.style.display = 'flex';
                    }}
                  />
                  <div className="project-image-placeholder-masonry" style={{ display: 'none' }}>
                    {project.title.charAt(0)}
                  </div>
                </motion.div>
              ))}
            </Masonry>
          ) : (
            <div className="project-detail-image">
              <img
                src={project.imageUrl}
                alt={project.title}
                className="project-detail-image-img"
              />
            </div>
          )}
        </div>
      </motion.section>

      {/* Project Overview */}
      <section className="project-detail-content">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h2 className="project-detail-section-title">Overview</h2>
            <p className="project-detail-description">
              {project.longDescription || project.description}
            </p>
          </motion.div>

          {/* Technologies */}
          <motion.div
            className="mt-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2 className="project-detail-section-title">Technologies Used</h2>
            <div className="project-detail-tech-grid">
              {project.technologies.map((tech, index) => (
                <div key={index} className="project-detail-tech-card">
                  <span className="project-detail-tech-name">{tech}</span>
                </div>
              ))}
            </div>
          </motion.div>

        </div>
      </section>

      {/* Image Lightbox */}
      <AnimatePresence>
        {lightboxImage !== null && project?.images && (
          <motion.div
            className="image-lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={closeLightbox}
          >
            <motion.div
              className="image-lightbox-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="image-lightbox-close"
                onClick={closeLightbox}
                aria-label="Close"
              >
                ×
              </button>

              <img
                src={project.images[lightboxImage]}
                alt={`${project.title} screenshot ${lightboxImage + 1}`}
                className="image-lightbox-image"
              />

              {project.images.length > 1 && (
                <>
                  <button
                    className="image-lightbox-nav image-lightbox-nav-prev"
                    onClick={prevImage}
                    aria-label="Previous image"
                  >
                    ←
                  </button>
                  <button
                    className="image-lightbox-nav image-lightbox-nav-next"
                    onClick={nextImage}
                    aria-label="Next image"
                  >
                    →
                  </button>
                  <div className="image-lightbox-counter">
                    {lightboxImage + 1} / {project.images.length}
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectDetail;
